/**
 * ThresholdFilter — reusable filter chip for numeric thresholds.
 *
 * Example:
 *   <ThresholdFilter
 *     label="Late arrival"
 *     unit="min"
 *     operator=">"
 *     value={30}
 *     onChange={(v) => setLateMin(v)}
 *     presets={[0, 15, 30, 60]}
 *   />
 */

import React, { useState } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';

export const ThresholdFilter = ({
  label,
  unit = '',
  operator = '>',
  value,
  onChange,
  presets = [],
  testid,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const active = value !== null && value !== undefined && value !== '';

  const commit = (next) => {
    setDraft(next);
    onChange?.(next === '' || next === null ? null : Number(next));
    setOpen(false);
  };

  return (
    <div className="relative inline-block" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors
          ${active
            ? 'bg-pink-50 text-pink-700 border-pink-300'
            : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300'}`}
      >
        <SlidersHorizontal className="w-3 h-3" />
        <span>
          {label} {active ? `${operator} ${value}${unit}` : ''}
        </span>
        {active && (
          <span
            onClick={(e) => { e.stopPropagation(); commit(''); }}
            className="ml-1 hover:bg-pink-100 rounded-full p-0.5"
            role="button"
            aria-label="Clear filter"
          >
            <X className="w-3 h-3" />
          </span>
        )}
      </button>
      {open && (
        <div className="absolute z-30 top-full mt-1 left-0 bg-white border border-neutral-200 rounded-lg shadow-lg p-3 w-56">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="text-xs text-neutral-500">{label} {operator}</span>
            <input
              type="number"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(draft); }}
              className="flex-1 px-2 py-1 text-xs border border-neutral-200 rounded"
              autoFocus
              data-testid={testid ? `${testid}-input` : undefined}
            />
            <span className="text-xs text-neutral-500">{unit}</span>
          </div>
          {presets.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {presets.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => commit(p)}
                  className="px-2 py-0.5 text-[10px] rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-700"
                >
                  {p === 0 ? 'Any' : `${operator}${p}${unit}`}
                </button>
              ))}
            </div>
          )}
          <div className="flex justify-between gap-1.5">
            <button
              onClick={() => commit('')}
              className="text-[11px] text-neutral-500 hover:text-neutral-800"
            >
              Clear
            </button>
            <button
              onClick={() => commit(draft)}
              className="px-2 py-0.5 text-[11px] bg-pink-600 text-white rounded"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThresholdFilter;
